import moment from 'moment';
import {
  EnvironmentVariableKeys,
  getEnvironmentVariable
} from '../environments/environment-variables';
import { getEnvironmentName } from '../environments/is-environment';
import { IContext, TeamType, UserTeamRole } from '../teams-sdk/teamsSdk';
import { SESSION_ID } from './session-context/SessionContext';
import {
  ContextColumns,
  EventBaseType,
  LogLevel,
  UserBiColumns
} from './Telemetry.constants';
import {
  IPanelActionData,
  IPanelActionEvent,
  IPanelViewData,
  IPanelViewEvent,
  ITelemetryContext
} from './Telemetry.types';
import {
  logException,
  logPageView,
  logScenario,
  logTrace,
  logUIEvent
} from './telemetryClient';
import {
  IMiddleTierRequestData,
  ITelemetryScenarioTrackerData,
  MiddleTierRequestEvent,
  ScenarioColumns,
  TelemetryScenarioName,
  TelemetryScenarioTrackerEvent
} from './TelemetryScenarioTracker.types';
import { getUserInfoTeamRole } from './utils/getCalculatedProperties';
import { getNavigationProperties } from './utils/getNavigationProperties';

/**
 * Logs messages and events to console and telemetry
 * NOTES: DO NOT USE THIS CLASS TO LOG ANY PII (Personally Identifiable Information)
 *
 * Traces:
 *  debug: logs traces with verbose level
 *  info: logs traces with info level
 *  log: logs a general trace message
 *  warn: logs traces with warning level
 *  error: logs error traces. If the error is fatally critical (the app cannot
 *  function completely), set the severity to Critical
 *
 * Exception: logs an exception that has been caught. This also logs an error trace
 * Event: logs an event, such as a user scenario like clicking the email button, going to the next step
 * Page View: logs that a page is opened
 * Metric: logs metrics for performance indicators, and for alerts
 */
export class Logger {
  private static userId: string | undefined;
  private static groupId: string | undefined;
  private static channelId: string | undefined;
  private static tId: string | undefined;
  private static teamId: string | undefined;
  private static teamType: TeamType | undefined;
  private static teamRole: UserTeamRole | undefined;
  private static hostClientType: string | undefined;
  private static locale: string | undefined;
  private static ringId: string | undefined;
  private static userLicenseType: string | undefined;

  public static initialize(context: IContext): void {
    Logger.userId = context.userObjectId;
    Logger.groupId = context.groupId;
    Logger.channelId = context.channelId;
    Logger.tId = context.tid;
    Logger.teamId = context.teamId;
    Logger.teamRole = context.userTeamRole;
    Logger.teamType = context.teamType;
    Logger.hostClientType = context.hostClientType;
    Logger.locale = context.locale;
    Logger.ringId = context.ringId;
    Logger.userLicenseType = context.userLicenseType;
  }

  /**
   * Logs a debug trace message to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static debug(
    message: string,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.debug(`[${moment.utc().format()}] ${message}`, properties);
    logTrace(message, LogLevel.Verbose, properties);
  }

  /**
   * Logs an info trace message to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static info(
    message: string,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.info(`[${moment.utc().format()}] ${message}`, properties);
    logTrace(message, LogLevel.Information, properties);
  }

  /**
   * Logs a trace message to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static log(
    message: string,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.log(`[${moment.utc().format()}] ${message}`, properties);
    logTrace(message, LogLevel.Information, properties);
  }

  /**
   * Logs a warning trace message to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static warn(
    message: string,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.warn(`[${moment.utc().format()}] ${message}`, properties);
    logTrace(message, LogLevel.Warning, properties);
  }

  /**
   * Logs an error trace message to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static error(
    message: string,
    severity?: LogLevel.Critical | LogLevel.Error,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.error(`[${moment.utc().format()}] ${message}`, properties);
    logTrace(message, severity, properties);
  }

  /**
   * Logs an exception that has been caught to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logException(
    error: Error,
    errorMessage?: string,
    severity?: LogLevel.Critical | LogLevel.Error,
    properties?: { [key: string]: string }
  ): void {
    const message = errorMessage ?? '';
    this._applyContextProperties(properties);

    console.error(`[${moment.utc().format()}] ${message}`, properties);
    console.error(error);
    logException(message, error, properties);
    logTrace(`${message}: ${error?.message}`, severity, properties);
  }

  /**
   * Logs a UI panel action telemetry to console and telemetry.
   * Should be used when a user performs actions on e.g. click, scroll, hover etc.
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logPanelActionEvent(properties: IPanelActionData): void {
    const panelActionProperties = Logger._getPanelActionColumns(properties);

    Logger._applyContextProperties(panelActionProperties);

    console.log(
      `[${moment.utc().format()}] ${EventBaseType.PanelAction}: `,
      panelActionProperties
    );
    logUIEvent<IPanelActionEvent>({
      eventName: EventBaseType.PanelAction,
      properties: panelActionProperties
    });
  }

  /**
   * Logs a UI panel view telemetry to console and telemetry
   * Should be used when a panel viewed is recorded from external/internal
   * triggers for e.g. page nivigation.
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logPanelViewEvent(properties: IPanelViewData): void {
    const panelViewProperties = Logger._getPanelViewColumns(properties);

    Logger._applyContextProperties(panelViewProperties);

    console.log(
      `[${moment.utc().format()}] ${EventBaseType.PanelView}`,
      panelViewProperties
    );
    logUIEvent<IPanelViewEvent>({
      eventName: EventBaseType.PanelView,
      properties: panelViewProperties
    });
  }

  /**
   * Logs a middle tier request response time scenario to the console and telemetry.
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logMiddleTierRequestScenario(
    properties: IMiddleTierRequestData
  ): void {
    const mtScenarioProperties = this._getMiddleTierRequestScenarioColumns(
      properties
    );

    this._applyContextProperties(mtScenarioProperties);

    console.log(
      `[${moment.utc().format()}] Scenario: ${
        mtScenarioProperties?.[ScenarioColumns.ScenarioName]
      }`,
      mtScenarioProperties
    );

    if (mtScenarioProperties.errorCode && mtScenarioProperties.error) {
      Logger.logException(
        mtScenarioProperties.error,
        `Scenario [${mtScenarioProperties[ScenarioColumns.ScenarioName]}] [${
          mtScenarioProperties.operation
        }] [${mtScenarioProperties.errorCode}]`,
        LogLevel.Error,
        {
          Scenario: mtScenarioProperties[ScenarioColumns.ScenarioName] ?? '',
          ScenarioType: mtScenarioProperties.operation ?? ''
        }
      );
    }

    logScenario<MiddleTierRequestEvent>({
      properties: mtScenarioProperties
    });
  }

  /**
   * Logs a UI scenario to the console and telemetry.
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logScenarioTrackerEvent(
    properties: ITelemetryScenarioTrackerData
  ): void {
    const eventProperties = this._getScenarioTrackerColumns(properties);

    this._applyContextProperties(eventProperties);

    console.log(
      `[${moment.utc().format()}] Scenario: ${
        eventProperties?.[ScenarioColumns.ScenarioName]
      }`,
      eventProperties
    );

    if (eventProperties.errorCode && eventProperties.error) {
      Logger.logException(
        eventProperties.error,
        `Scenario [${eventProperties[ScenarioColumns.ScenarioName]}] [${
          eventProperties.scenarioType
        }]`,
        LogLevel.Error,
        {
          Scenario: eventProperties?.[ScenarioColumns.ScenarioName] ?? '',
          ScenarioType: eventProperties.scenarioType ?? ''
        }
      );
    }

    logScenario<TelemetryScenarioTrackerEvent>({
      properties: eventProperties
    });
  }

  /**
   * Logs that a page, or a container is displayed to the user to console and telemetry
   * NOTES: DO NOT LOG PII (Personally Identifiable Information)
   */
  public static logPageView(
    pageName: string,
    pageCategory?: string,
    uri?: string,
    refUri?: string,
    properties?: { [key: string]: string }
  ): void {
    this._applyContextProperties(properties);

    console.log(
      `[${moment.utc().format()}] Page View: ${pageName}`,
      properties
    );
    logPageView(pageName, pageCategory, uri, refUri, properties);
  }

  /**
   * Appends telemetry common properties to the event log.
   * @param properties - event properties.
   */
  private static _applyContextProperties<T>(
    properties?: T & Partial<ITelemetryContext>
  ): void {
    if (!properties) {
      properties = {} as T;
    }

    const environment = getEnvironmentName();
    const teamsAppId = getEnvironmentVariable(
      EnvironmentVariableKeys.teamsAppId
    );
    const navigatorProperties = getNavigationProperties();

    properties[ContextColumns.AppInfoEnvironment] = environment;
    properties[ContextColumns.UserInfoId] = Logger.userId ?? '';
    properties[ContextColumns.TeamOfficeGroupId] = Logger.groupId ?? '';
    properties[ContextColumns.ChannelId] = Logger.channelId ?? '';
    properties[ContextColumns.UserInfoTeamRole] = getUserInfoTeamRole(
      Logger.teamRole
    );
    properties[ContextColumns.UserInfoTenantId] = Logger.tId ?? '';
    properties[ContextColumns.TeamId] = Logger.teamId ?? '';
    properties[ContextColumns.TeamType] = Logger.teamType?.toString() ?? '';
    properties[ContextColumns.SessionId] = SESSION_ID;
    properties[ContextColumns.AppId] = teamsAppId ?? '';
    properties[ContextColumns.AppInfoClientType] = Logger.hostClientType ?? '';
    properties[ContextColumns.AppInfoLocale] = Logger.locale ?? '';
    properties[ContextColumns.UserInfoRing] = Logger.ringId ?? '';
    properties[ContextColumns.UserInfoLicenseType] =
      Logger.userLicenseType ?? '';
    properties[ContextColumns.AppInfoPlatformId] =
      String(navigatorProperties.appPlatformId) ?? '';
    properties[ContextColumns.UserAgent] = navigatorProperties.userAgent ?? '';
  }

  /**
   * Transforms panel view data to align with business telemetry schema.
   * @param data - panel view data.
   * @returns transformed panel view event data.
   */
  private static _getPanelViewColumns(data: IPanelViewData): IPanelViewEvent {
    const { panelRegion, panelType, panelLaunchMethod, databag } = data;
    const event: IPanelViewEvent = {
      [UserBiColumns.PanelRegion]: panelRegion,
      [UserBiColumns.PanelType]: panelType,
      ...databag
    };

    if (panelLaunchMethod) {
      event[UserBiColumns.PanelLaunchMethod] = panelLaunchMethod;
    }

    return event;
  }

  /**
   * Transforms data to align with business telemetry schema for panel action event.
   * @param data - panel action properties
   * @returns transformed panel action event data.
   */
  private static _getPanelActionColumns(
    data: IPanelActionData
  ): IPanelActionEvent {
    const {
      actionGesture,
      actionOutcome,
      actionScenario,
      actionScenarioType,
      moduleName,
      moduleSummary,
      databag
    } = data;

    return {
      [UserBiColumns.Gesture]: actionGesture,
      [UserBiColumns.ActionOutcome]: actionOutcome,
      [UserBiColumns.Scenario]: actionScenario,
      [UserBiColumns.ScenarioType]: actionScenarioType,
      [UserBiColumns.ModuleName]: moduleName,
      [UserBiColumns.ModuleSummary]: moduleSummary,
      ...Logger._getPanelViewColumns(data),
      ...databag
    };
  }

  /**
   * Extracts only the peroperties to be pushed down to telemetry.
   * @param data  - middle tier request properties.
   * @returns middle tier request scenario data columns.
   */
  private static _getMiddleTierRequestScenarioColumns(
    data: IMiddleTierRequestData
  ): MiddleTierRequestEvent {
    const {
      [ScenarioColumns.CorrelationId]: correlationId,
      [ScenarioColumns.ScenarioStatus]: status,
      elapsed,
      error,
      errorCode,
      operation
    } = data;

    return {
      [ScenarioColumns.ScenarioName]: TelemetryScenarioName.MiddleTierRequest,
      [ScenarioColumns.CorrelationId]: correlationId,
      [ScenarioColumns.ScenarioStatus]: status,
      elapsed,
      operation,
      error,
      errorCode
    };
  }

  /**
   * Extracts only the peroperties to be pushed down to telemetry.
   * @param data - page load request data.
   * @returns page load scenario columns.
   */
  private static _getScenarioTrackerColumns(
    data: ITelemetryScenarioTrackerData
  ): TelemetryScenarioTrackerEvent {
    const {
      [ScenarioColumns.ScenarioName]: scenarioName,
      [ScenarioColumns.ScenarioStep]: step,
      [ScenarioColumns.ScenarioStatus]: status,
      elapsed,
      error,
      errorCode,
      scenarioType,
      delta,
      stepDelta,
      message,
      outcome,
      eventNumber,
      id
    } = data;

    return {
      [ScenarioColumns.ScenarioName]: scenarioName,
      [ScenarioColumns.ScenarioStep]: step,
      [ScenarioColumns.ScenarioStatus]: status,
      delta,
      stepDelta,
      message,
      outcome,
      elapsed,
      error,
      errorCode,
      scenarioType,
      eventNumber,
      id
    };
  }
}
